import React from "react";
import { Box, Grid, Chip, Divider, Typography } from "@mui/material";
import {
    InfoOutlined,
    DateRangeOutlined,
    DescriptionOutlined,
    DirectionsCarOutlined,
    Person,
    PaymentsOutlined,
    PlaceOutlined,
    VerifiedOutlined,
    HistoryOutlined,
} from "@mui/icons-material";
import { useVehicleHiringEntryDetailContext } from "contexts/VehicleHiringEntryDetailContext";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";

const HiringDatesSummary = ({ dates }) => {
    const sorted = [...dates].sort((a, b) => new Date(a) - new Date(b));
    const formatted = sorted.map((d) =>
        new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    );
    return (
        <Box>
            <span style={{ fontSize: "0.9rem" }}>{formatted.join(", ")}</span>
            <div style={{ fontSize: "0.75rem", color: "#7F8C8D", marginTop: "2px" }}>
                {sorted.length} day{sorted.length !== 1 ? "s" : ""}
            </div>
        </Box>
    );
};

const VehicleHiringEntryBasicDetail = () => {
    const { vehicleHiringEntryDetail, loadingVehicleHiringEntry } =
        useVehicleHiringEntryDetailContext();

    const renderAuditSubtitle = (user, date) => (
        <Box>
            <div style={{ fontSize: "0.9rem" }}>{user?.name || "N/A"}</div>
            {date && (
                <div style={{ fontSize: "0.75rem", color: "#7F8C8D", marginTop: "2px" }}>
                    {localizeDateTimeShort(date)}
                </div>
            )}
        </Box>
    );

    return !loadingVehicleHiringEntry ? (
        <Box padding={3}>
            <Grid container spacing={4}>
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <DirectionsCarOutlined color="primary" />}
                        title="Vehicle Reg. Number"
                        subtitle={vehicleHiringEntryDetail?.vehicleRegistrationNumber?.registration_number || "N/A"}
                    />
                </Grid>
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <PlaceOutlined color="primary" />}
                        title="Origin"
                        subtitle={vehicleHiringEntryDetail?.sourceBranch?.name || "N/A"}
                    />
                </Grid>
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <PlaceOutlined color="primary" />}
                        title="Destination"
                        subtitle={vehicleHiringEntryDetail?.destinationBranch?.name || "N/A"}
                    />
                </Grid>

                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <DirectionsCarOutlined color="primary" />}
                        title="Vehicle Type"
                        subtitle={vehicleHiringEntryDetail?.vehicleType || "N/A"}
                    />
                </Grid>
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <InfoOutlined color="primary" />}
                        title="Hiring Type"
                        subtitle={vehicleHiringEntryDetail?.hiringType || "N/A"}
                    />
                </Grid>

                {/* Separated Owner Details */}
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <Person color="primary" />}
                        title="Owner Name & Phone"
                        subtitle={
                            <Box>
                                <div style={{ fontSize: "0.9rem" }}>
                                    {vehicleHiringEntryDetail?.ownerName || "N/A"}
                                </div>
                                <div style={{ fontSize: "0.75rem", color: "#7F8C8D", marginTop: "2px" }}>
                                    {vehicleHiringEntryDetail?.ownerNumber || "N/A"}
                                </div>
                            </Box>
                        }
                    />
                </Grid>

                {/* Separated Driver Details */}
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <Person color="primary" />}
                        title="Driver Name & Phone"
                        subtitle={
                            <Box>
                                <div style={{ fontSize: "0.9rem" }}>
                                    {vehicleHiringEntryDetail?.driverName || "N/A"}
                                </div>
                                <div style={{ fontSize: "0.75rem", color: "#7F8C8D", marginTop: "2px" }}>
                                    {vehicleHiringEntryDetail?.driverNumber || "N/A"}
                                </div>
                            </Box>
                        }
                    />
                </Grid>

                {/* Approve / Submitted To */}
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <Person color="primary" />}
                        title="Approve / Submitted To"
                        subtitle={vehicleHiringEntryDetail?.responsibleUser?.name || "N/A"}
                    />
                </Grid>

                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <PaymentsOutlined color="primary" />}
                        title="Hiring Fare"
                        subtitle={vehicleHiringEntryDetail?.hiringFare ?? "N/A"}
                    />
                </Grid>
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <PaymentsOutlined color="primary" />}
                        title="Total Fare"
                        subtitle={vehicleHiringEntryDetail?.totalFare ?? "N/A"}
                    />
                </Grid>
                <Grid item xs={3}>
                    <InformationTile
                        icon={() => <VerifiedOutlined color="primary" />}
                        title="Approval Status"
                        subtitle={
                            <Box display="flex" flexDirection="column" alignItems="start" mt={1}>
                                <Chip
                                    label={vehicleHiringEntryDetail?.approvalStatus || "Pending"}
                                    size="small"
                                    color={
                                        vehicleHiringEntryDetail?.approvalStatus === "Approved"
                                            ? "success"
                                            : vehicleHiringEntryDetail?.approvalStatus === "Rejected"
                                                ? "error"
                                                : "warning"
                                    }
                                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                                />
                            </Box>
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <InformationTile
                        icon={() => <DescriptionOutlined color="primary" />}
                        title="Purpose of Hiring"
                        subtitle={vehicleHiringEntryDetail?.purposeOfHiring || "N/A"}
                    />
                </Grid>

                <Grid item xs={12}>
                    <InformationTile
                        icon={() => <DateRangeOutlined color="primary" />}
                        title="Hiring Dates"
                        subtitle={
                            vehicleHiringEntryDetail?.hiringDates?.length ? (
                                <HiringDatesSummary dates={vehicleHiringEntryDetail.hiringDates} />
                            ) : (
                                "N/A"
                            )
                        }
                    />
                </Grid>

                {/* Bank Account Details (Single Object) */}
                <Grid item xs={12}>
                    <Box mt={1}>
                        <strong style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                            <PaymentsOutlined color="primary" /> Bank Account Details:
                        </strong>
                        {vehicleHiringEntryDetail?.bankAccountDetails?.bankName || vehicleHiringEntryDetail?.bankAccountDetails?.accountNumber ? (
                            <div style={{ fontSize: "0.9rem", marginBottom: "4px" }}>
                                <strong>Bank:</strong> {vehicleHiringEntryDetail.bankAccountDetails.bankName || "N/A"} <br />
                                <strong>Account No:</strong> {vehicleHiringEntryDetail.bankAccountDetails.accountNumber || "N/A"}
                            </div>
                        ) : (
                            <span style={{ fontSize: "0.9rem" }}>N/A</span>
                        )}
                    </Box>
                </Grid>

                {/* Attendance Information Section */}
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DateRangeOutlined color="primary" /> Total Hiring Day/s
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={4}>
                            <InformationTile
                                icon={() => <DateRangeOutlined color="primary" />}
                                title="Total Days"
                                subtitle={vehicleHiringEntryDetail?.attendanceSummary?.totalDays ?? "N/A"}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InformationTile
                                icon={() => <DateRangeOutlined color="primary" />}
                                title="Working Days"
                                subtitle={vehicleHiringEntryDetail?.attendanceSummary?.workingDays ?? "N/A"}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InformationTile
                                icon={() => <DateRangeOutlined color="primary" />}
                                title="Absent Days"
                                subtitle={vehicleHiringEntryDetail?.attendanceSummary?.absentDays ?? "N/A"}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                {/* Audit Information Section */}
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <HistoryOutlined color="primary" /> Audit Information
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={4}>
                            <InformationTile
                                icon={() => <Person color="primary" />}
                                title="Created By"
                                subtitle={renderAuditSubtitle(vehicleHiringEntryDetail?.createdBy, vehicleHiringEntryDetail?.createdAt)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InformationTile
                                icon={() => <Person color="primary" />}
                                title="Updated By"
                                subtitle={renderAuditSubtitle(vehicleHiringEntryDetail?.updatedBy, vehicleHiringEntryDetail?.updatedAt)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InformationTile
                                icon={() => <Person color="primary" />}
                                title="Approved By"
                                subtitle={renderAuditSubtitle(vehicleHiringEntryDetail?.reviewedBy, vehicleHiringEntryDetail?.reviewedAt)}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    ) : (
        <InfoGridSkeleton />
    );
};

export default VehicleHiringEntryBasicDetail;
